import { Modal } from "antd";
import { Link } from "react-router-dom";
import { PopupDelete, PopupUpdate } from "./icons";
import { RotateLeftIcon } from "./svg-action-icons";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PermissionDebugger from "./PermissionDebugger";
import {
  faPrint,
  faEye,
  faPlus,
  faFileCirclePlus,
  faFileCircleMinus,
  faUndo,
  faMoneyBillTransfer,
  faClipboardCheck,
  faCheckDouble,
  faCalendarPlus,
  faCreditCard,
  faHourglassHalf,
  faFileContract,
  faDownload,
  faBoxOpen,
  faExchangeAlt,
  faBoxes,
  faFileExport,
  faFileImport,
  faRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";

import { LOGOUT_ALL_DEVICES_ACTION_TYPE } from "constants/general";

const { confirm } = Modal;

const getIconColor = (type) => {
  const colorMap = {
    Delete: "rgb(var(--color-danger))",
    Update: "rgb(var(--color-primary))",
    View: "rgb(var(--color-success))",
    Print: "rgb(var(--color-accent-purple))",
    AddService: "rgb(var(--color-primary))",
    AddCreditNote: "rgb(var(--color-success))",
    AddDebitNote: "rgb(var(--color-warning))",
    AddSalesRefund: "rgb(var(--color-danger))",
    AddPurchaseRefund: "rgb(var(--color-warning))",
    PayDues: "rgb(var(--color-success))",
    Complete: "rgb(var(--color-success))",
    FullFilled: "rgb(var(--color-success))",
    AddPayment: "rgb(var(--color-accent-purple))",
    AddPaymentMethod: "rgb(var(--color-accent-purple))",
    Pending: "rgb(var(--color-warning))",
    ViewContract: "rgb(var(--color-primary))",
    Download: "rgb(var(--color-accent-purple))",
    Receive: "rgb(var(--color-success))",
    Transfer: "rgb(var(--color-primary))",
    Supply: "rgb(var(--color-success))",
    Export: "rgb(var(--color-accent-purple))",
    Import: "rgb(var(--color-primary))",
    [LOGOUT_ALL_DEVICES_ACTION_TYPE]: "rgb(var(--color-danger))",
  };
  return colorMap[type] || "rgb(var(--color-secondary))";
};

const getIconBackground = (type) => {
  const backgroundMap = {
    Delete: "#FEE2E2", // Light red
    Update: "var(--color-primary-bg-soft)",
    View: "#D1FAE5", // Light green
    Print: "#EDE9FE", // Light purple
    AddService: "#CFFAFE", // Light cyan
    AddCreditNote: "#CCFBF1", // Light teal
    AddDebitNote: "#FEF3C7", // Light amber
    AddSalesRefund: "#FCE7F3", // Light pink
    AddPurchaseRefund: "#FFEDD5", // Light orange
    PayDues: "#D1FAE5", // Light green
    Complete: "#DCFCE7", // Light green
    FullFilled: "#D1FAE5", // Light green
    AddPayment: "#E0E7FF", // Light indigo
    AddPaymentMethod: "#EDE9FE", // Light purple
    Pending: "#FEF3C7", // Light yellow
    ViewContract: "var(--color-primary-bg-soft)",
    Download: "#E0E7FF", // Light indigo
    Receive: "#D1FAE5", // Light green
    Transfer: "#CFFAFE", // Light cyan
    Supply: "#CCFBF1", // Light teal
    Export: "#EDE9FE", // Light purple
    Import: "var(--color-primary-bg-soft)",
    [LOGOUT_ALL_DEVICES_ACTION_TYPE]: "var(--color-danger-bg-soft)",
  };
  return backgroundMap[type] || "#F3F4F6";
};

const Icon = ({ type }) => {
  const iconMap = {
    Delete: <PopupDelete />,
    Update: <PopupUpdate />,
    View: <FontAwesomeIcon icon={faEye} />,
    Print: <FontAwesomeIcon icon={faPrint} />,
    AddService: <FontAwesomeIcon icon={faPlus} />,
    AddCreditNote: <FontAwesomeIcon icon={faFileCirclePlus} />,
    AddDebitNote: <FontAwesomeIcon icon={faFileCircleMinus} />,
    AddSalesRefund: <FontAwesomeIcon icon={faUndo} />,
    AddPurchaseRefund: <FontAwesomeIcon icon={faUndo} />,
    PayDues: <FontAwesomeIcon icon={faMoneyBillTransfer} />,
    Complete: <FontAwesomeIcon icon={faClipboardCheck} />,
    FullFilled: <FontAwesomeIcon icon={faCheckDouble} />,
    AddPayment: <FontAwesomeIcon icon={faCalendarPlus} />,
    AddPaymentMethod: <FontAwesomeIcon icon={faCreditCard} />,
    Pending: <FontAwesomeIcon icon={faHourglassHalf} />,
    ViewContract: <FontAwesomeIcon icon={faFileContract} />,
    Download: <FontAwesomeIcon icon={faDownload} />,
    Receive: <FontAwesomeIcon icon={faBoxOpen} />,
    Transfer: <FontAwesomeIcon icon={faExchangeAlt} />,
    Supply: <FontAwesomeIcon icon={faBoxes} />,
    Export: <FontAwesomeIcon icon={faFileExport} />,
    Import: <FontAwesomeIcon icon={faFileImport} />,
    Restore: <RotateLeftIcon />,
    [LOGOUT_ALL_DEVICES_ACTION_TYPE]: (
      <FontAwesomeIcon icon={faRightFromBracket} />
    ),
  };

  return iconMap[type] || null;
};

const getConfirmConfig = (t) => ({
  Delete: {
    title: t("delete_popup"),
    okText: t("okTitle"),
    cancelText: t("cancelTitle"),
  },
  Terminate: {
    title: t("terminate_contract_confirmation"),
    okText: t("okTitle"),
    cancelText: t("cancelTitle"),
  },
  UnlinkEjar: {
    title: t("ejar.actions.unlinkConfirm"),
    okText: t("okTitle"),
    cancelText: t("cancelTitle"),
  },
  Restore: {
    title: t("softDelete.restoreConfirm"),
    okText: t("okTitle"),
    cancelText: t("cancelTitle"),
  },
  [LOGOUT_ALL_DEVICES_ACTION_TYPE]: {
    title: t("logoutAllDevices.confirm"),
    okText: t("okTitle"),
    cancelText: t("cancelTitle"),
  },
  Clone: {
    title: t("clone.confirm_title"),
    okText: t("clone.confirm_ok"),
    cancelText: t("clone.confirm_cancel"),
  },
});

const handleActionClick = (action, type, t) => {
  if (typeof action !== "function") return;

  const config = getConfirmConfig(t)[type];

  if (!config) {
    action();
    return;
  }

  confirm({
    ...config,
    onOk: action,
    onCancel() {},
  });
};
const CrudOptions = ({ actions }) => {
  const { t } = useTranslation();
  return (
    <div className="py-1">
      {(actions || []).map((a, index) => {
        if (!a.type) return null;

        const optionClassName =
          "flex gap-3 min-w-[160px] px-3 items-center cursor-pointer py-2.5 rounded-lg hover:bg-surface-muted dark:hover:bg-gray-800 transition-all duration-150 group focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary";

        const optionContent = (
          <>
            <div
              className="flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 group-hover:scale-110 shadow-sm"
              style={{
                color: getIconColor(a.type),
                backgroundColor: getIconBackground(a.type),
              }}
            >
              <Icon type={a.type} />
            </div>
            <p
              className="font-medium text-sm transition-colors duration-150"
              style={{ color: getIconColor(a.type) }}
            >
              {a.text}
            </p>
          </>
        );

        // An action that only opens a page carries `to` and is a real link.
        const optionItem = a.to ? (
          <Link to={a.to} className={optionClassName}>
            {optionContent}
          </Link>
        ) : (
          <div
            className={optionClassName}
            onClick={() => handleActionClick(a.action, a.type, t)}
          >
            {optionContent}
          </div>
        );

        return a.permissionId ? (
          <PermissionDebugger key={a.type} permissionId={a.permissionId}>
            {optionItem}
          </PermissionDebugger>
        ) : (
          <div key={a.type}>{optionItem}</div>
        );
      })}
    </div>
  );
};

export default CrudOptions;
